import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import { Printer, Download, X } from 'lucide-react';
import QRCode from 'qrcode';
import type { Band } from '../types/database';

interface QRFlyerModalProps {
  band: Band;
  isOpen: boolean;
  onClose: () => void;
}

export function QRFlyerModal({ band, isOpen, onClose }: QRFlyerModalProps) {
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && band) {
      generateQRCode();
    }
  }, [isOpen, band]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      const bandUrl = `https://jamnation.ch/bands/${band.slug}`;
      const qrDataUrl = await QRCode.toDataURL(bandUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#0a0a0a',
          light: '#ffffff'
        }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const flyerElement = document.getElementById('qr-flyer-content');
    if (!flyerElement) return;

    // Create a canvas to render the flyer
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size (A4 proportions)
    canvas.width = 794; // A4 width at 96 DPI
    canvas.height = 1123; // A4 height at 96 DPI

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add band image, name, and QR code
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Draw band image (centered, square)
      const imageSize = 300;
      const imageX = (canvas.width - imageSize) / 2;
      const imageY = 100;
      ctx.drawImage(img, imageX, imageY, imageSize, imageSize);

      // Draw band name
      ctx.fillStyle = '#0a0a0a';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(band.name, canvas.width / 2, imageY + imageSize + 80);

      // Draw QR code
      if (qrCodeUrl) {
        const qrImg = new Image();
        qrImg.onload = () => {
          const qrSize = 200;
          const qrX = (canvas.width - qrSize) / 2;
          const qrY = imageY + imageSize + 120;
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

          // Draw URL text
          ctx.font = '24px Arial';
          ctx.fillText('jamnation.ch', canvas.width / 2, qrY + qrSize + 50);

          // Download the canvas as image
          const link = document.createElement('a');
          link.download = `${band.name}-flyer.png`;
          link.href = canvas.toDataURL();
          link.click();
        };
        qrImg.src = qrCodeUrl;
      }
    };
    img.src = band.avatar_url || '';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Print styles */}
      <style jsx global>{`
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
          
          html, body {
            height: auto !important;
            overflow: visible !important;
          }
          
          body * {
            visibility: hidden !important;
          }
          
          #qr-flyer-content,
          #qr-flyer-content * {
            visibility: visible !important;
          }
          
          #qr-flyer-content {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: white !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 2cm !important;
            box-sizing: border-box !important;
            page-break-after: avoid !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-band-name {
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          .print-genre-pill {
            background-color: #e60050 !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          .print-qr-border {
            border: 3px solid #000000 !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          .print-band-image {
            width: 200px !important;
            height: 200px !important;
          }
          
          .print-qr-code {
            width: 150px !important;
            height: 150px !important;
          }
          
          .print-title {
            font-size: 36px !important;
            margin: 20px 0 !important;
          }
          
          .print-genres {
            margin: 15px 0 !important;
          }
          
          .print-qr-section {
            margin-top: 30px !important;
          }
        }
      `}</style>

      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="space-y-6">
          {/* Header with actions */}
          <div className="flex justify-between items-center no-print">
            <h2 className="text-2xl font-bold text-light">{t('qrFlyer.title')}</h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={handlePrint}
                className="btn-outline flex items-center space-x-2"
                disabled={loading}
              >
                <Printer className="w-5 h-5" />
                <span>{t('qrFlyer.print')}</span>
              </button>
              <button
                onClick={handleDownload}
                className="btn-outline flex items-center space-x-2"
                disabled={loading || !qrCodeUrl}
              >
                <Download className="w-5 h-5" />
                <span>{t('qrFlyer.download')}</span>
              </button>
            </div>
          </div>

          {/* Flyer Content */}
          <div 
            id="qr-flyer-content"
            className="bg-white rounded-lg p-12 text-center min-h-[600px] flex flex-col justify-center items-center space-y-6"
            style={{ 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }}
          >
            {/* Band Image */}
            {band.avatar_url && (
              <div className="relative">
                <div 
                  className="w-64 h-64 rounded-full overflow-hidden shadow-2xl print-band-image"
                  style={{
                    background: 'linear-gradient(to right, rgb(5, 173, 134), rgb(5, 128, 173))',
                    padding: '4px'
                  }}
                >
                  <img
                    src={band.avatar_url}
                    alt={band.name}
                    className="w-full h-full object-cover rounded-full bg-white"
                   loading="lazy" />
                </div>
              </div>
            )}

            {/* Band Name */}
            <div className="space-y-2">
              <h1 
                className="text-5xl font-bold text-gray-900 print-band-name print-title"
                style={{ 
                  fontFamily: '"Tilt Warp", cursive',
                  color: '#000000'
                }}
              >
                {band.name}
              </h1>
              {band.location && (
                <p className="text-xl text-gray-600">{band.location}</p>
              )}
            </div>

            {/* Genres */}
            {band.genres && band.genres.length > 0 && (
              <div className="flex flex-wrap justify-center gap-3 print-genres">
                {band.genres.slice(0, 4).map((genre) => (
                  <span
                    key={genre}
                    className="px-4 py-2 bg-primary text-white rounded-full text-sm font-medium print-genre-pill"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {/* QR Code */}
            {loading ? (
              <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : qrCodeUrl ? (
              <div className="space-y-4 print-qr-section">
                <div 
                  className="p-4 bg-white rounded-xl shadow-lg inline-block"
                  style={{ 
                    border: '3px solid #000000'
                  }}
                >
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="w-48 h-48 print-qr-code"
                   loading="lazy" />
                </div>
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-gray-900">{t('qrFlyer.scanForInfo')}</p>
                  <p 
                    className="text-2xl font-bold"
                    style={{ 
                      fontFamily: '"Tilt Warp", cursive',
                      color: '#e60050'
                    }}
                  >
                    jamnation.ch
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">{t('qrFlyer.qrError')}</p>
              </div>
            )}

            {/* Footer */}
            <div className="pt-8 border-t border-gray-200 w-full">
              <p className="text-sm text-gray-500">
                {t('qrFlyer.footer')}
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="no-print bg-gray-custom/10 rounded-lg p-4">
            <h3 className="font-semibold text-light mb-2">{t('qrFlyer.usage.title')}</h3>
            <ul className="text-sm text-light/80 space-y-1">
              <li>• <strong>{t('qrFlyer.usage.print')}:</strong> {t('qrFlyer.usage.printDescription')}</li>
              <li>• <strong>{t('qrFlyer.usage.download')}:</strong> {t('qrFlyer.usage.downloadDescription')}</li>
              <li>• <strong>{t('qrFlyer.usage.qrCode')}:</strong> {t('qrFlyer.usage.qrDescription')}</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
}